import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth0 } from '@auth0/auth0-react';
import { getConfig } from '../config';
import Loading from '../components/Loading';
import ErrorPage from '../views/ErrorPage';
import { AUTH0_SCOPE } from './config';

const DriverSessionContext = createContext(null);
const { audience } = getConfig();

// Only failures that require interaction with Auth0 should initiate login.
// Network errors and access_denied can persist across logins, so redirecting
// for those errors would risk a loop.
export const requiresLogin = (error) => {
  const code = String(error?.error || '').toLowerCase();
  return [
    'login_required',
    'missing_refresh_token',
    'invalid_grant',
    'consent_required',
    'interaction_required',
    'mfa_required',
  ].includes(code) || /^(Missing Refresh Token|Consent required)/i.test(error?.message || '');
};

export const useDriverSession = () => {
  const session = useContext(DriverSessionContext);
  if (!session) {
    throw new Error('useDriverSession must be used within <DriverSession>.');
  }
  return session;
};

export default function DriverSession({ children }) {
  const { isLoading, isAuthenticated, user, error, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const subject = user?.sub;
  const [session, setSession] = useState({ status: 'checking' });
  const loginPromise = useRef(null);

  const redirectToLogin = useCallback(() => {
    // Several API requests may discover the same expired session at once.
    // Hide protected content immediately and share one login transaction.
    // Keep the latch until navigation replaces this document. An explicit
    // Retry reloads both this boundary and Auth0's initialization state.
    if (!loginPromise.current) {
      setSession({ status: 'redirecting' });
      loginPromise.current = Promise.resolve().then(() => loginWithRedirect({
        appState: {
          returnTo: window.location.pathname + window.location.search + window.location.hash,
        },
      })).catch((loginError) => {
        setSession({ status: 'error', error: loginError });
        throw loginError;
      });
    }
    return loginPromise.current;
  }, [loginWithRedirect]);

  const getAccessToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience,
          scope: AUTH0_SCOPE,
          redirect_uri: window.location.origin,
        },
        detailedResponse: true,
      });
    } catch (tokenError) {
      if (requiresLogin(tokenError)) {
        await redirectToLogin();
      }
      throw tokenError;
    }
  }, [getAccessTokenSilently, redirectToLogin]);

  useEffect(() => {
    if (isLoading) return undefined;
    let active = true;

    const restoreSession = async () => {
      if (error) {
        if (requiresLogin(error)) {
          await redirectToLogin();
        } else {
          setSession({ status: 'error', error });
        }
        return;
      }
      if (!isAuthenticated) {
        await redirectToLogin();
        return;
      }

      setSession({ status: 'checking' });
      // Auth0's isAuthenticated can be true for a cached user even when
      // checkSession swallowed a failed renewal. Verify token availability
      // before mounting Main and starting its API requests.
      await getAccessToken();
      if (active && !loginPromise.current) {
        setSession({ status: 'ready', subject });
      }
    };

    restoreSession().catch((sessionError) => {
      if (active && !requiresLogin(sessionError)) {
        setSession({ status: 'error', error: sessionError });
      }
    });
    return () => { active = false; };
  }, [isLoading, isAuthenticated, subject, error, getAccessToken, redirectToLogin]);

  // Handle a newly reported SDK error during render, before the effect runs,
  // so protected children never need their own authentication error gate.
  if (session.status === 'error' || (error && !requiresLogin(error))) {
    return <ErrorPage
      title='Unable to verify your session'
      message='Please try again. If the problem continues, return to login.'
      // Restart SDK initialization without logging out or clearing its cache.
      // This also recovers from SDK errors that survive a silent-token call.
      onRetry={() => window.location.reload()}
    />;
  }
  if (isLoading || !isAuthenticated || error || session.status !== 'ready' || session.subject !== subject) {
    return <Loading />;
  }

  return (
    <DriverSessionContext.Provider value={{ getAccessToken }}>
      {children}
    </DriverSessionContext.Provider>
  );
}

DriverSession.propTypes = { children: PropTypes.node.isRequired };
